import { bookshelfGroups } from "@shared/bookshelf";
import { BookOpen, Folder, Layers3, List, UserRound } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@web/components/ui/button";
import { ScrollArea } from "@web/components/ui/scroll-area";
import type { BookshelfViewItem } from "../../../api";
import type { BookDetailPanel } from "../../../shelf/types";

export function BookDetailCard({
  item,
  activePanel,
  busy,
  currentChapterLabel,
  detailError,
  detailLoading,
  groupId,
  selectedSourceName,
  selectedSourceLatestChapterAt,
  selectedSourceLatestChapter,
  selectedSourceAvailable,
  secondaryActionLabel = "删除书籍",
  secondaryActionVariant = "destructive",
  onCatalogToggle,
  onOpen,
  onPanelChange,
  onSecondaryAction,
}: {
  item: BookshelfViewItem;
  activePanel: BookDetailPanel;
  busy: boolean;
  currentChapterLabel: string;
  detailError?: string | null;
  detailLoading?: boolean;
  groupId: string;
  selectedSourceName: string;
  selectedSourceLatestChapterAt?: string;
  selectedSourceLatestChapter?: string;
  selectedSourceAvailable: boolean;
  secondaryActionLabel?: string;
  secondaryActionVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  onCatalogToggle: () => void;
  onOpen: () => void;
  onPanelChange: (panel: BookDetailPanel) => void;
  onSecondaryAction: () => void;
}) {
  const latestChapterLabel = selectedSourceLatestChapter ?? "未知最新章";

  return (
    <section
      className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-2xl border bg-background shadow-2xl"
      onClick={event => event.stopPropagation()}
    >
      <BookDetailHeader item={item} latestChapterAt={selectedSourceLatestChapterAt} />

      <ScrollArea className="max-h-[calc(92vh-9rem)] p-4 sm:p-6">
        <div className="grid gap-3 text-sm text-muted-foreground">
          <BookMetaLine icon={<UserRound className="size-4" />} label="作者" value={item.book.author ?? "未知作者"} />
          <BookMetaActionLine
            active={activePanel === "source"}
            disabled={busy || item.sources.length === 0}
            icon={<Layers3 className="size-4" />}
            label="书源"
            value={selectedSourceName}
            onClick={() => onPanelChange(activePanel === "source" ? null : "source")}
          />
          <BookMetaLine icon={<BookOpen className="size-4" />} label="最新" value={latestChapterLabel} />
          <BookMetaActionLine
            active={activePanel === "group"}
            disabled={busy}
            icon={<Folder className="size-4" />}
            label="分组"
            value={bookshelfGroups.find(group => group.id === groupId)?.label ?? "未分组"}
            onClick={() => onPanelChange(activePanel === "group" ? null : "group")}
          />
          <BookMetaActionLine
            active={activePanel === "catalog"}
            disabled={busy || !selectedSourceAvailable}
            icon={<List className="size-4" />}
            label="目录"
            value={currentChapterLabel}
            onClick={onCatalogToggle}
          />
        </div>

        <ScrollArea className="mt-4 max-h-36 rounded-xl border bg-muted/20 p-3 text-sm leading-7 text-foreground/75">
          {item.book.description ?? (detailLoading ? "正在加载详情..." : detailError ?? "暂无简介")}
        </ScrollArea>

        <div className="mt-4 flex gap-2">
          <Button type="button" variant={secondaryActionVariant} className="flex-1" disabled={busy} onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </Button>
          <Button type="button" className="flex-1" disabled={busy} onClick={onOpen}>
            阅读
          </Button>
        </div>
      </ScrollArea>
    </section>
  );
}

function BookDetailHeader({ item, latestChapterAt }: { item: BookshelfViewItem; latestChapterAt?: string }) {
  return (
    <div className="relative overflow-hidden border-b bg-muted/30 px-4 pt-5 pb-4 sm:px-6">
      {item.book.coverUrl && (
        <img src={item.book.coverUrl} alt="" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-15 blur-xl" loading="lazy" />
      )}
      <div className="relative flex gap-4">
        <BookDetailCover title={item.book.title} coverUrl={item.book.coverUrl} />
        <div className="flex min-h-32 min-w-0 flex-1 flex-col py-1">
          <h2 className="line-clamp-2 text-xl font-semibold tracking-tight">{item.book.title}</h2>
          {Boolean(item.book.tags?.length) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {item.book.tags?.slice(0, 4).map(tag => (
                <span key={tag} className="rounded-full border bg-background/70 px-2 py-0.5 text-[11px] text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="mt-auto grid gap-1 text-xs text-muted-foreground">
            <BookTimeLine label="更新时间" value={formatBookTime(latestChapterAt)} />
            <BookTimeLine label="最后阅读" value={formatBookTime(item.progress?.updatedAt)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function BookTimeLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2">
      <span>{label}</span>
      <span className="truncate text-foreground/70">{value}</span>
    </div>
  );
}

function formatBookTime(value?: string): string {
  if (!value) return "-";
  const timestamp = Date.parse(value.includes("T") ? value : value.replace(" ", "T"));
  if (Number.isNaN(timestamp)) return value;

  return formatBookDateTime(new Date(timestamp));
}

export function formatBookDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hour = padDatePart(date.getHours());
  const minute = padDatePart(date.getMinutes());

  return `${year}-${month}-${day} ${hour}:${minute}`;
}

function padDatePart(value: number): string {
  return value.toString().padStart(2, "0");
}

function BookDetailCover({ title, coverUrl }: { title: string; coverUrl?: string }) {
  return (
    <div className="h-32 w-24 shrink-0 overflow-hidden rounded-lg border bg-muted shadow-lg">
      {coverUrl ? (
        <img src={coverUrl} alt={title} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-muted-foreground">
          <BookOpen className="size-7" />
        </span>
      )}
    </div>
  );
}

function BookMetaLine({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="grid grid-cols-[1rem_3rem_minmax(0,1fr)] items-center gap-2">
      <span className="text-muted-foreground">{icon}</span>
      <span>{label}</span>
      <span className="truncate text-foreground/80">{value}</span>
    </div>
  );
}

function BookMetaActionLine({
  active,
  disabled,
  icon,
  label,
  value,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className="group grid grid-cols-[1rem_3rem_minmax(0,1fr)] items-center gap-2 py-1.5 text-left disabled:opacity-50"
      onClick={onClick}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`truncate rounded-lg py-1 transition-colors ${
          active ? "bg-accent text-accent-foreground" : "text-foreground/80 group-hover:bg-accent/60"
        }`}
      >
        {value}
      </span>
    </button>
  );
}
